import { Box, chakra, useRecipe } from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { ButtonBarRootProps } from '@components/ui/buttonBar/ButtonBar';
import { Tooltip } from '@components/ui/tooltip';
import { keyframes } from '@emotion/react';
import { forwardRef } from 'react';

const NOTCH_SIZE = 12;
const NOTCH_OFFSET = NOTCH_SIZE - 2;
const DIAGONAL = NOTCH_SIZE * Math.sqrt(2);

export const RawButtonBarRoot = forwardRef<HTMLButtonElement, ButtonBarRootProps>(
  (
    {
      children,
      isHidden = false,
      notch = true,
      notchPlacement = 'bottom-right',
      hideTooltip = false,
      tooltipPlacement = 'top',
      colorPalette = 'neutralBlue',
      shouldFlicker = false,
      ...props
    },
    ref,
  ) => {
    const recipe = useRecipe({ key: 'buttonBar' });
    const [variantProps, rest] = recipe.splitVariantProps(props as ButtonBarRootProps);
    const styles = recipe(variantProps);

    const flicker = keyframes`
      0%, 100% {
        background-color: ${styles['@layer recipes']?.backgroundColor};
        color: ${styles['@layer recipes']?.color};
      }
      50% {
        background-color: ${styles['@layer recipes']?.['&[data-active="true"]']?.backgroundColor};
        color: ${styles['@layer recipes']?.['&[data-active="true"]']?.color};
      }
    `;

    if (isHidden) {
      return null;
    }

    return (
      <Tooltip content={props['aria-label']} positioning={{ placement: tooltipPlacement }} disabled={hideTooltip}>
        <chakra.button
          ref={ref}
          {...styles}
          {...rest}
          colorPalette={colorPalette}
          animation={shouldFlicker ? `${flicker} 1s steps(1, end) infinite` : undefined}
          className={cx(styles, 'group', rest.className)}
        >
          {children}
          {notch && (
            <Box
              position="absolute"
              content="''"
              width={DIAGONAL}
              height={DIAGONAL}
              backgroundColor="neutral.800"
              transform="rotate(45deg)"
              transition="none"
              _groupHover={{ backgroundColor: 'skyBlue' }}
              top={notchPlacement === 'top-left' || notchPlacement === 'top-right' ? `-${NOTCH_OFFSET}px` : 'auto'}
              bottom={
                notchPlacement === 'bottom-left' || notchPlacement === 'bottom-right' ? `-${NOTCH_OFFSET}px` : 'auto'
              }
              left={notchPlacement === 'top-left' || notchPlacement === 'bottom-left' ? `-${NOTCH_OFFSET}px` : 'auto'}
              right={
                notchPlacement === 'top-right' || notchPlacement === 'bottom-right' ? `-${NOTCH_OFFSET}px` : 'auto'
              }
            />
          )}
        </chakra.button>
      </Tooltip>
    );
  },
);
